import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { environment } from '@environments/environment';

import { MensagemService } from './../../../../../shared/services/mensagem.service';
import { LocalidadeService } from '@app/shared/services';
import { UtilService } from '@app/shared/services/util.service';

import { Censo, CEP, ChaveValor, ConfigCamposFormulario, Loading, Municipio, Uf } from '@app/shared/models';
import { TipoLogradouro } from '@app/shared/models/enums/tipo-logradouro.enum';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-form-endereco',
  templateUrl: './form-endereco.component.html',
  styleUrls: ['./form-endereco.component.scss']
})
export class FormEnderecoComponent implements OnInit, OnChanges, OnDestroy {

  @Input() formEtapa3: FormGroup;
  @Input() ufEnderecoId: number;
  @Input() configCamposFormulario: ConfigCamposFormulario[];
  @Input() censo: Censo;

  cliente = environment.cliente;

  carregar = new Loading();
  carregarMunicipios = new Loading();
  estados: Uf[] = [];
  todosMunicipios: Municipio[] = [];
  municipios: Municipio[] = [];

  enumTipoLogradouro = new TipoLogradouro();

  private destroy$ = new Subject<void>();

  constructor(
    private localidadeService: LocalidadeService,
    private mensagemService: MensagemService,
    private utilService: UtilService
  ) {}


  ngOnInit(): void {
    this.obterUfs();
    this.validarEnderecoInternacional();
  }

  ngOnChanges(simpleChanges: SimpleChanges): void {
    if (simpleChanges.formEtapa3?.currentValue) {
      const uf = this.formEtapa3.get('ufEnderecoId')?.value;
      if (uf) {
        this.lidarComUf(uf);
      }
    }
  }

  private validarEnderecoInternacional(): void {
    this.formEtapa3.get('possuiEnderecoInternacional')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        (value === true) ? this.limparCamposEnderecoNacional() : this.limparCampoEnderecoInternacional();
      });
  }

  private limparCamposEnderecoNacional(): void {
    const campos = [
      'tipoLogradouroEndereco',
      'endereco',
      'numero',
      'ufEnderecoId',
      'municipioEnderecoId',
      'bairro',
      'cepEndereco',
      'complemento'
    ];

    campos.forEach(campo => {
      const control = this.formEtapa3.get(campo);
      if (control) {
        control.setValue(null);
        control.updateValueAndValidity();
      }
    });

    this.municipios = [];
  }

  private limparCampoEnderecoInternacional(): void {
    const control = this.formEtapa3.get('enderecoInternacional');
    if (control) {
      control.setValue(null);
      control.updateValueAndValidity();
    }
  }

  lidarComUf(uf: any): void {
    const ufControl = this.formEtapa3.get('ufEnderecoId');
    const municipioControl = this.formEtapa3.get('municipioEnderecoId');

    if (!uf || uf === 'null' || uf === '') {
      this.municipios = [];
      ufControl?.setValue(null);
      municipioControl?.setValue(null);
      return;
    }

    this.localidadeService.getMunicipios(uf).subscribe(municipios => {
      this.municipios = municipios;

      const municipioSelecionado = municipioControl?.value;
      const existeMunicipio = municipios.some(mun => mun.id === municipioSelecionado);

      if (!existeMunicipio) {
        municipioControl?.setValue(null);
      }
    });
  }

  obterUfs(): void {
    this.localidadeService.getEstados(this.carregar).subscribe(estados => {
      this.estados = estados;
    });
  }

  buscarCEP() {
    const cep = this.formEtapa3.get('cepEndereco').value

    this.localidadeService.getCep(cep, this.carregar).subscribe(cep => {
      if (!cep.status) {
        return this.mensagemService.avisoMensagemAlerta(cep.erro, 'Não foi possível localizar as informações');
      }

      this.popularLocalidade(cep);
    })
  }

  visualizarMapa(): void {
    const logradouro = this.formEtapa3.get('endereco').value
    const numero = this.formEtapa3.get('numero').value
    const bairro = this.formEtapa3.get('bairro').value
    const uf = this.estados.find(_ => _.id == this.formEtapa3.get('ufEnderecoId').value);
    const cidade = this.todosMunicipios.find(_ => _.id == this.formEtapa3.get('municipioEnderecoId').value);

    if ((logradouro && numero && bairro && uf && cidade) == null) {
      return this.mensagemService.avisoMensagemAlerta('Para visualizar o mapa é necessário preencher todas as informações')
    }

    const endereco = `${logradouro} ${numero}, ${bairro} - ${cidade.nome}/${uf.nome}`;

    window.open(`https://www.google.com.br/maps/search/${encodeURI(endereco)}`, '_blank').focus();
  }

  private popularLocalidade(cep: CEP) {
    this.lidarComUf(cep.result.idUf);

    // * Ex.: 'Avenida Paulo Bastos' => 'AVENIDA'
    const tipoLogradouroCEP = cep.result.logradouro.split(' ')[0].toUpperCase();
    const tipoLogradouro = this.enumTipoLogradouro.lista.filter(_ => _.nome == tipoLogradouroCEP);

    this.formEtapa3.patchValue({
      bairro: cep?.result?.bairro,
      endereco: this.validarLogradouro(cep?.result?.logradouro, tipoLogradouro).toUpperCase() || '',
      ufEnderecoId: cep?.result?.idUf,
      municipioEnderecoId: cep?.result?.idMunicipio,
      tipoLogradouroEndereco: this.validarTipoLogradouro(tipoLogradouro)
    });
  }

  private validarTipoLogradouro(tipoLogradouro: ChaveValor[]) {
    if (tipoLogradouro.length == 0) return null;
    return tipoLogradouro[0].id
  }

  private validarLogradouro(logradouro: string, tipoLogradouro: ChaveValor[]) {
    if (tipoLogradouro.length == 0) return logradouro;
    // * Ex.: 'Avenida Paulo Bastos' => 'Paulo Bastos'
    return logradouro.replace(tipoLogradouro[0].label, '');
  }

  isRequired(campo: string): any {
    return this.utilService.isRequiredField(campo, this.formEtapa3)
  }

  isVisible(campo: string): boolean {
    if (!this.configCamposFormulario) return null;

    const isVisible = this.configCamposFormulario.find((_: any) => _.nomeCampo == campo).visivel
    return isVisible;
  }

  get verificarForm(): any {
    return this.utilService.verificarForm(this.formEtapa3);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
